/**
 * Left-margin sizing for charts that paint a rotated y-axis unit label
 * (α, dB, Amp, …) beside right-aligned y tick numbers.
 *
 * Tick anchors sit `Y_AXIS_TICK_GAP` px left of `plotX`; the unit label is
 * centred at `Y_AXIS_UNIT_CENTER_X` in the canvas margin. `pad.l` must be
 * large enough that the widest tick string never overlaps the unit glyph on
 * narrow viewports (panel absorption at 100px tall, impulse response Amp, …).
 */
import { canvasType } from '@roomtreatment/design';
import type { ChartPad } from './layout';

/** Gap between the plot's left edge and the y tick number anchor (px). */
export const Y_AXIS_TICK_GAP = 6;

/** Centre x of the rotated y-axis unit label in canvas coordinates. */
export const Y_AXIS_UNIT_CENTER_X = 9;

/** The rotated unit label is set in the canvas scale's `axis` role (11px
 *  medium, `@roomtreatment/design`) — its horizontal extent when rotated ≈
 *  the font size. Read from the role, never restated, so a scale change moves
 *  the margin with it. */
export const Y_AXIS_UNIT_FONT_PX = canvasType.axis.size;

/** Minimum gap between the unit label's right edge and tick numbers (px). */
export const Y_AXIS_LABEL_SPACING = 5;

/** Gap between the canvas edge and the tick numbers on an axis that carries
 *  no unit caption beside them (px). */
export const Y_AXIS_EDGE_GAP = 4;

export interface YAxisPadOptions {
  /** False for an axis with no rotated unit caption beside its numbers — a
   *  unitless index such as STI. Default true. */
  unitLabel?: boolean;
}

export function computeMinLeftPadForYAxis(
  yTickLabels: readonly string[],
  measureWidth: (label: string) => number,
  { unitLabel = true }: YAxisPadOptions = {},
): number {
  const maxTickW = yTickLabels.reduce((m, label) => Math.max(m, measureWidth(label)), 0);
  const beside = unitLabel
    ? Y_AXIS_UNIT_CENTER_X + Y_AXIS_UNIT_FONT_PX / 2 + 1 + Y_AXIS_LABEL_SPACING
    : Y_AXIS_EDGE_GAP;
  return Math.ceil(beside + maxTickW + Y_AXIS_TICK_GAP);
}

/** Raise `pad.l` when tick labels would collide with the rotated unit caption. */
export function inflatePadForYAxis(
  pad: ChartPad,
  yTickLabels: readonly string[],
  measureWidth: (label: string) => number,
  opts: YAxisPadOptions = {},
): ChartPad {
  const minL = computeMinLeftPadForYAxis(yTickLabels, measureWidth, opts);
  return pad.l >= minL ? pad : { ...pad, l: minL };
}

export interface YAxisTickLabels {
  /** The left axis's tick labels. */
  left: readonly string[];
  /** A twin axis's tick labels, painted on the right edge as the left one's
   *  mirror image: numbers `Y_AXIS_TICK_GAP` right of the plot, the unit
   *  caption centred `Y_AXIS_UNIT_CENTER_X` in from the canvas's right edge. */
  right?: readonly string[];
}

/**
 * Build a chart on a pad wide enough for its y-axis text.
 *
 * The tick labels come out of the geometry and the geometry needs the pad, so
 * this builds once on `basePad` and rebuilds only when that pad is too narrow
 * for the labels it produced — measured with the renderer's own `measureWidth`
 * at the font it will paint them in. A pad sized by hand for one font is how
 * tick numbers ended up printed over their axis's unit when the type grew.
 * Only ever widens; an axis with no labels (empty data) leaves its side alone.
 */
export function withFittedYAxisPad<T>(
  basePad: ChartPad,
  measureWidth: (label: string) => number,
  build: (pad: ChartPad) => T,
  labels: (built: T) => YAxisTickLabels,
  opts: YAxisPadOptions = {},
): T {
  const built = build(basePad);
  const { left, right } = labels(built);
  const l = left.length
    ? Math.max(basePad.l, computeMinLeftPadForYAxis(left, measureWidth, opts))
    : basePad.l;
  const r = right?.length
    ? Math.max(basePad.r, computeMinLeftPadForYAxis(right, measureWidth, opts))
    : basePad.r;
  return l === basePad.l && r === basePad.r ? built : build({ ...basePad, l, r });
}
